import { formatCurrency } from "@/utils/formatCurrency.js";

/**
 * Todo lo que se muestra de un paquete sale de GET /packages: precio, folios,
 * vigencia y banderas. La landing y /buy_package usan estas mismas funciones,
 * así que no pueden mostrar datos distintos. Nada de números mágicos: las
 * cintas, los ahorros y los "NUEVO EN ESTE PLAN" se deducen comparando cada
 * paquete con el anterior del catálogo, no preguntando por el paquete 4.
 */

/** Precio del paquete en pesos cerrados: $230, $1,800. */
export const formatCents = (cents) =>
  formatCurrency(cents / 100, "es-MX", "MXN", 0, 0);

/**
 * El diseño pinta el signo de pesos como superíndice, así que el precio se
 * entrega partido: { symbol: "$", amount: "1,800" }.
 */
export const priceParts = (cents) => ({
  symbol: "$",
  amount: new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(
    cents / 100,
  ),
});

/** Costo por folio, siempre con centavos: $23.00, $18.33. */
export const formatPerFolio = (cents) =>
  formatCurrency(cents / 100, "es-MX", "MXN", 2, 2);

/** Capacidades booleanas en el orden en que se listan; la clave es la del API. */
export const CAPABILITY_KEYS = [
  "storage",
  "fiscalCertificate",
  "complianceOpinion",
  "history",
];

/** Capacidades con cupo: se muestran con el número cuando hay. */
export const QUOTA_KEYS = ["advisories", "consultations"];

/** El catálogo se ordena por precio: el más chico es la referencia de ahorro. */
export const sortCatalog = (packages) =>
  [...(packages ?? [])].sort((a, b) => a.priceCents - b.priceCents);

/** Paquete de referencia: el de mayor costo por folio (el más pequeño). */
export const referencePackage = (catalog) =>
  catalog.reduce(
    (worst, pkg) =>
      !worst || pkg.pricePerFolioCents > worst.pricePerFolioCents ? pkg : worst,
    null,
  );

/**
 * Ahorro contra el costo por folio del paquete de referencia, en porcentaje
 * entero. 0 en el propio paquete de referencia.
 */
export const savingsPercent = (pkg, catalog) => {
  const base = referencePackage(catalog)?.pricePerFolioCents;
  if (!base || pkg.pricePerFolioCents >= base) return 0;
  return Math.round((1 - pkg.pricePerFolioCents / base) * 100);
};

/** Ancho de la barra de precio por folio: 100 % en el paquete de referencia. */
export const barWidthPercent = (pkg, catalog) => {
  const base = referencePackage(catalog)?.pricePerFolioCents;
  if (!base) return 100;
  return Math.round((pkg.pricePerFolioCents / base) * 100);
};

export const foliosLabel = (pkg, t) =>
  t("packages.folios", { count: pkg.creditedFolios });

/** "Pagas 40, recibes 50", sólo cuando el paquete bonifica folios. */
export const bonusLabel = (pkg, t) =>
  pkg.bonusFolios > 0
    ? t("packages.bonus", {
        paid: pkg.paidFolios,
        credited: pkg.creditedFolios,
      })
    : null;

/**
 * API Platform omite los null, así que un paquete ilimitado no trae
 * validityMonths: la fuente de verdad es unlimitedValidity.
 *
 * @param {string} prefix "validity" para las tarjetas ("Vigencia de folios:
 *   1 año") o "value" para el comparativo ("1 año").
 */
export const validityLabel = (pkg, t, prefix = "validity") => {
  if (pkg.unlimitedValidity) return t(`packages.${prefix}Unlimited`);
  const months = pkg.validityMonths;
  return months % 12 === 0
    ? t(`packages.${prefix}Years`, { count: months / 12 })
    : t(`packages.${prefix}Months`, { count: months });
};

export const perFolioLabel = (pkg, t) =>
  t("packages.perFolio", { price: formatPerFolio(pkg.pricePerFolioCents) });

/** Un paquete "todo incluido" es el que no deja ningún renglón fuera. */
export const isAllIncluded = (pkg) =>
  CAPABILITY_KEYS.every((key) => Boolean(pkg[key])) &&
  QUOTA_KEYS.every((key) => pkg[key] > 0) &&
  Boolean(pkg.priorityAttention);

/**
 * Cinta de la tarjeta: lo único que esta gama estrena frente a la anterior.
 * @param {object|null} previous paquete inmediatamente más barato
 */
export const ribbonLabel = (pkg, previous, t) => {
  if (isAllIncluded(pkg)) return t("packages.ribbonAll");
  if (pkg.bonusFolios > 0)
    return t("packages.ribbonBonus", { count: pkg.bonusFolios });
  if (pkg.unlimitedValidity && previous && !previous.unlimitedValidity)
    return t("packages.ribbonNoExpiry");
  return null;
};

/**
 * Renglones de "qué incluye" de una tarjeta. `isNew` marca lo que este paquete
 * estrena frente al anterior, que es lo que la tarjeta etiqueta como
 * "NUEVO EN ESTE PLAN".
 *
 * @returns {{key: string, label: string, included: boolean, isNew: boolean}[]}
 */
export const packageFeatures = (pkg, previous, t) => [
  {
    key: "folios",
    label: foliosLabel(pkg, t),
    included: true,
    isNew: Boolean(previous) && pkg.creditedFolios > previous.creditedFolios,
  },
  {
    key: "validity",
    label: validityLabel(pkg, t),
    included: true,
    isNew:
      Boolean(pkg.unlimitedValidity) &&
      Boolean(previous) &&
      !previous.unlimitedValidity,
  },
  {
    key: "attention",
    label: pkg.priorityAttention
      ? t("packages.attentionPriority")
      : t("packages.attention"),
    included: true,
    isNew:
      Boolean(pkg.priorityAttention) &&
      Boolean(previous) &&
      !previous.priorityAttention,
  },
  {
    key: "cfdi",
    label: t("packages.cfdiKept"),
    included: true,
    isNew: false,
  },
  ...CAPABILITY_KEYS.map((key) => ({
    key,
    label: t(`packages.${key}`),
    included: Boolean(pkg[key]),
    isNew: Boolean(pkg[key]) && Boolean(previous) && !previous[key],
  })),
  ...QUOTA_KEYS.map((key) => ({
    key,
    label:
      pkg[key] > 0
        ? t(`packages.${key}Count`, { count: pkg[key] })
        : t(`packages.${key}`),
    included: pkg[key] > 0,
    isNew: Boolean(previous) && pkg[key] > (previous[key] ?? 0),
  })),
];

const nameList = (packages, locale, type) =>
  new Intl.ListFormat(locale, { style: "long", type }).format(
    packages.map((pkg) => pkg.shortName ?? pkg.name),
  );

/** "Del Paquete 30 en adelante…": el primer paquete de vigencia ilimitada. */
const firstUnlimited = (catalog) =>
  catalog.find((pkg) => pkg.unlimitedValidity) ?? null;

/**
 * Las preguntas frecuentes se escriben con los datos del catálogo: si mañana
 * cambian precios o vigencias, las respuestas siguen diciendo la verdad.
 */
export const packageFaq = (catalog, t, locale = "es-MX") => {
  const limited = catalog.filter((pkg) => !pkg.unlimitedValidity);
  const unlimited = catalog.filter((pkg) => pkg.unlimitedValidity);
  const bonus = catalog.find((pkg) => pkg.bonusFolios > 0) ?? null;
  const first = firstUnlimited(catalog);
  const limitedOr = nameList(limited, locale, "disjunction");
  const limitedAnd = nameList(limited, locale, "conjunction");

  const items = [
    limited.length > 0 &&
      unlimited.length > 0 && {
        key: "1",
        question: t("packages.faq1Q"),
        answer: t("packages.faq1A", {
          limited: limitedAnd,
          unlimited: nameList(unlimited, locale, "conjunction"),
        }),
      },
    {
      key: "2",
      question: t("packages.faq2Q"),
      answer: t("packages.faq2A", { limited: limitedAnd }),
    },
    bonus && {
      key: "3",
      question: t("packages.faq3Q", {
        package: bonus.shortName ?? bonus.name,
      }),
      answer: t("packages.faq3A", {
        price: formatCents(bonus.priceCents),
        paid: bonus.paidFolios,
        credited: bonus.creditedFolios,
        advisories: bonus.advisories,
      }),
    },
    first && {
      key: "4",
      question: t("packages.faq4Q"),
      answer: t("packages.faq4A", {
        small: limitedOr,
        unlimited: first.shortName ?? first.name,
      }),
    },
  ];

  return items.filter(Boolean);
};
